import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useLeaderboard } from "../api/hooks";
import { getUser } from "../auth/session";
import type { LeaderboardCategory, LeaderboardEntry, LeaderboardScope } from "../api/types";
import { RankRow } from "../components/RankRow";
import { Card, ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { theme } from "../theme";

const SCOPES: { id: LeaderboardScope; label: string }[] = [
  { id: "chat", label: "this chat" },
  { id: "global", label: "global" },
  { id: "chats", label: "chats" },
];

const CATEGORIES: { id: LeaderboardCategory; label: string; icon: string; color: string }[] = [
  { id: "caregiver", label: "caregiver", icon: "◯", color: theme.color.satiety },
  { id: "solver", label: "solver", icon: "?", color: theme.color.happiness },
  { id: "quest", label: "quest", icon: "⌬", color: theme.color.accent },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(4)};
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Note = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

// Entry to the geo map — a tappable cousin of GlobalBoard's dashed Note card.
const MapEntry = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surfaceAlt};
  border: 1px dashed ${({ theme }) => theme.color.borderHi};
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.color.textDim};
  text-align: left;
  cursor: pointer;

  .icon {
    flex-shrink: 0;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 13px;
    color: ${({ theme }) => theme.color.accent};
  }

  .text {
    flex: 1;

    b {
      color: ${({ theme }) => theme.color.text};
      font-weight: 600;
    }
  }

  .go {
    flex-shrink: 0;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 11px;
    color: ${({ theme }) => theme.color.accent};
  }
`;

// Scope: a segmented control with an accent-filled active tab.
const Segment = styled.div`
  display: flex;
  padding: 3px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const Seg = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 9px 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $active }) => ($active ? theme.color.accent : "transparent")};
  color: ${({ theme, $active }) => ($active ? "#fff" : theme.color.textDim)};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  letter-spacing: 0.02em;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.color.textFaint};
    cursor: not-allowed;
  }
`;

// Category: per-category colored pills, horizontally scrollable.
const Cats = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const CatPill = styled.button<{ $active: boolean; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme, $active, $color }) => ($active ? `${$color}22` : theme.color.surface)};
  border: 1px solid ${({ theme, $active, $color }) => ($active ? `${$color}55` : theme.color.border)};
  color: ${({ theme, $active }) => ($active ? theme.color.text : theme.color.textDim)};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
`;

const CatIcon = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 13px;
`;

// Podium: height-varying gradient plinths, ordered 2 · 1 · 3.
const PodiumWrap = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 10px;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 82px;
  min-width: 0;
`;

const PAvatar = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.surfaceRaised};
  border: 2px solid ${({ $color }) => $color};
  color: #fff;
  font-family: ${({ theme }) => theme.font.mono};
  font-weight: 700;
  font-size: 14px;
`;

const PName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.text};
  margin-top: 6px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PScore = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

const Bar = styled.div<{ $color: string; $h: number }>`
  width: 100%;
  height: ${({ $h }) => $h}px;
  margin-top: 6px;
  padding: 8px 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: linear-gradient(180deg, ${({ $color }) => `${$color}33`}, ${({ $color }) => `${$color}11`});
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-bottom: none;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Empty = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.color.textFaint};
  padding: ${({ theme }) => theme.space(4)} 0;
  text-align: center;
`;

// "You" marker — a free-standing accent card when the viewer is off-list.
const YouMarker = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.3);
`;

const YRank = styled.span`
  width: 26px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.accent};
`;

const YAvatar = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.accent};
  color: #fff;
  font-family: ${({ theme }) => theme.font.mono};
  font-weight: 700;
  font-size: 12px;
`;

const YName = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
`;

const YScore = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
`;

const MAX_NAME_LENGTH = 10;
const truncateName = (name: string) =>
  [...name].length > MAX_NAME_LENGTH ? `${[...name].slice(0, MAX_NAME_LENGTH).join("")}…` : name;

const initialOf = (name: string) => (name.replace(/^@/, "").trim()[0] ?? "?").toUpperCase();

const PLINTH = {
  1: { h: 108, color: theme.color.accent },
  2: { h: 88, color: theme.color.textDim },
  3: { h: 68, color: theme.color.happiness },
} as const;

const Crown = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" style={{ marginBottom: 2 }}>
    <path
      d="M1 13L3 4L7 8L10 1L13 8L17 4L19 13Z"
      fill="#FFB020"
      stroke="#FFB020"
      strokeWidth="0.8"
      strokeLinejoin="round"
    />
  </svg>
);

function PodiumStep({ entry, place }: { entry: LeaderboardEntry; place: 1 | 2 | 3 }) {
  const { h, color } = PLINTH[place];
  return (
    <Step>
      {place === 1 ? <Crown /> : <span style={{ height: 16 }} />}
      <PAvatar $color={color}>{initialOf(entry.displayName)}</PAvatar>
      <PName>{truncateName(entry.displayName)}</PName>
      <PScore>{entry.score.toLocaleString()}</PScore>
      <Bar $color={color} $h={h}>
        {place}
      </Bar>
    </Step>
  );
}

export function Leaderboard({
  initialScope = "chat",
  lockChatScope = false,
  showMapEntry = true,
}: {
  /** Scope selected on first render (the DM global board starts at "global"). */
  initialScope?: LeaderboardScope;
  /** Disable the "this chat" scope — used when there is no chat context. */
  lockChatScope?: boolean;
  /** Hide the geo-map banner — the DM shell has no router, so /board/map can't render there. */
  showMapEntry?: boolean;
} = {}) {
  const navigate = useNavigate();
  const [scope, setScope] = useState<LeaderboardScope>(initialScope);
  const [category, setCategory] = useState<LeaderboardCategory>("caregiver");
  const { data, isLoading, error } = useLeaderboard(scope, category);
  const myId = getUser()?.id;

  const isYou = (e: LeaderboardEntry) => myId !== undefined && e.userId === myId;

  // Podium holds top 3; keep the flat list to rank 4+ so it isn't duplicated.
  const [first, second, third] = data?.podium ?? [];
  const hasPodium = Boolean(first && second && third);
  const listEntries = data
    ? hasPodium
      ? data.entries.filter((e) => e.rank > 3)
      : data.entries
    : [];
  // Visual order: 2nd, 1st, 3rd.
  const podiumOrder: { entry: LeaderboardEntry; place: 1 | 2 | 3 }[] =
    first && second && third
      ? [
          { entry: second, place: 2 },
          { entry: first, place: 1 },
          { entry: third, place: 3 },
        ]
      : [];

  return (
    <Stack>
      <Header>
        <SectionLabel>// leaderboard</SectionLabel>
        <Note>all-time</Note>
      </Header>

      {showMapEntry && (
        <MapEntry type="button" onClick={() => navigate("/board/map")}>
          <span className="icon">◈</span>
          <span className="text">
            <b>raccoons worldwide</b> — see who's on the map
          </span>
          <span className="go">map →</span>
        </MapEntry>
      )}

      <Segment>
        {SCOPES.map((s) => (
          <Seg
            key={s.id}
            $active={scope === s.id}
            disabled={lockChatScope && s.id === "chat"}
            onClick={() => setScope(s.id)}
          >
            {s.label}
          </Seg>
        ))}
      </Segment>

      <Cats>
        {CATEGORIES.map((c) => (
          <CatPill
            key={c.id}
            $active={category === c.id}
            $color={c.color}
            onClick={() => setCategory(c.id)}
          >
            <CatIcon $color={c.color}>{c.icon}</CatIcon>
            {c.label}
          </CatPill>
        ))}
      </Cats>

      {isLoading && <Loading label="loading ranks…" />}
      {error && <ErrorNotice message={error.message} />}

      {data && (
        <>
          {hasPodium && (
            <PodiumWrap>
              {podiumOrder.map(({ entry, place }) => (
                <PodiumStep key={`${entry.rank}-${entry.displayName}`} entry={entry} place={place} />
              ))}
            </PodiumWrap>
          )}

          <div>
            <Header>
              <SectionLabel>// ranks</SectionLabel>
              <Note>full table</Note>
            </Header>
            <Card>
              {listEntries.length === 0 ? (
                <Empty>no scores yet — be the first</Empty>
              ) : (
                <List>
                  {listEntries.map((e) => (
                    <RankRow key={`${e.rank}-${e.displayName}`} entry={e} you={isYou(e)} />
                  ))}
                </List>
              )}
            </Card>
          </div>

          {data.viewer && !data.entries.some(isYou) && (
            <YouMarker>
              <YRank>{data.viewer.rank}</YRank>
              <YAvatar>Y</YAvatar>
              <YName>you</YName>
              <YScore>{data.viewer.score.toLocaleString()}</YScore>
            </YouMarker>
          )}
        </>
      )}
    </Stack>
  );
}
